import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Calendar } from 'primereact/calendar'
import { InputText } from 'primereact/inputtext'
import FormDialog from '../../../components/ui/FormDialog'
import FormField, { FormGrid } from '../../../components/ui/FormField'
import { formatDate, parseISODate, todayStart, toISODate } from '../../../lib/datetime'
import type { DeadlineRequest } from '../types'

interface Props {
  /** İşlenecek talep; `null` → pop-up kapalı. */
  request: DeadlineRequest | null
  submitting: boolean
  onSubmit: (approvedDeadline: string) => void
  onHide: () => void
}

/**
 * **Talebi onayla** pop-up'ı (087/US2).
 *
 * <p>Tarih alanı **talep edilen tarihle dolu** açılır (FR-015) ve onay mercii isterse değiştirir.
 * Takvim **bugünden öncesini seçtirmez** (FR-016); mevcut terminden önceye çekmek **serbesttir**
 * (FR-009a) — bu yüzden alt sınır bugündür, mevcut termin değil.
 *
 * <p><b>Talep edilen tarih takvimde seçilemez</b> (kullanıcı kararı 2026-08-14): alan zaten o tarihle
 * dolu açıldığı için "talebi olduğu gibi onayla" akışı **tek tıkla** (Onayla) çalışır; takvimi açmak
 * ise tarihi *değiştirme* niyetidir, o yüzden aynı tarih orada devre dışıdır. Kural bilinçli olarak
 * yalnız **seçim** katmanındadır — ön-dolu değer geçerli kalır ve sunucu da onu kabul eder.
 *
 * <p>Onaylandığında aksiyonun **yalnız termin tarihi** güncellenir; talebin `requestedDate` alanı
 * **değişmez** (FR-020a), dolayısıyla "ne istendi / ne onaylandı" farkı listede görünür kalır.
 */
export default function ApproveDialog({ request, submitting, onSubmit, onHide }: Readonly<Props>) {
  const { t } = useTranslation()
  const [date, setDate] = useState<Date | null>(null)

  // Açılışta talep edilen tarihi getir (FR-015). Talep değişince yeniden kurulur.
  useEffect(() => {
    setDate(request ? parseISODate(request.requestedDate) : null)
  }, [request])

  const invalid = date == null

  /**
   * Takvimde devre dışı bırakılacak günler: **talep edilen** tarih (yukarıdaki javadoc) ve aksiyonun
   * **mevcut** termini (kullanıcı kararı 2026-08-14 — o tarihi onaylamak hiçbir şeyi değiştirmezdi).
   * İkisi de yalnız *seçimi* engeller; ön-dolu değer geçerli kalır ve sunucu onu kabul eder.
   */
  const disabledDates = [
    parseISODate(request?.requestedDate ?? null),
    parseISODate(request?.currentDeadline ?? null),
  ].filter((d): d is Date => d != null)

  const submit = () => {
    if (invalid || submitting) return
    onSubmit(toISODate(date))
  }

  return (
    <FormDialog
      visible={request != null}
      header={t('deadlineRequests.approve.title')}
      submitLabel={t('deadlineRequests.approve.submit')}
      submitting={submitting}
      submitDisabled={invalid}
      onSubmit={submit}
      onHide={onHide}
    >
      <FormGrid>
        {/* Aksiyon adı BAŞLIKTA değil formun içinde durur (kullanıcı kararı 2026-08-14): başlık kısa
            kalır, uzun aksiyon adı iki satıra sarıp başlığı taşırmaz ve düzen ret pop-up'ıyla aynıdır. */}
        <FormField
          label={t('deadlineRequests.field.actionName')}
          htmlFor="deadline-approve-action"
          full
        >
          <InputText
            id="deadline-approve-action"
            value={request?.actionName ?? ''}
            readOnly
            disabled
          />
        </FormField>

        <FormField
          label={t('deadlineRequests.column.currentDeadline')}
          htmlFor="deadline-approve-current"
          full
        >
          {/* Salt-okunur bağlam: onay mercii neyi değiştirdiğini görebilsin (elle CSS sınıfı değil,
              PrimeReact InputText — görünüm tema/override katmanından gelir). */}
          <InputText
            id="deadline-approve-current"
            value={formatDate(request?.currentDeadline)}
            readOnly
            disabled
          />
        </FormField>

        <FormField
          label={t('deadlineRequests.field.requestedDate')}
          htmlFor="deadline-approve-date"
          required
          full
        >
          <Calendar
            inputId="deadline-approve-date"
            value={date}
            onChange={(e) => setDate((e.value as Date | null) ?? null)}
            dateFormat="dd.mm.yy"
            minDate={todayStart()}
            // Alt aksiyonda üst terminden sonrası seçilemez (066); kökte sınır yok. Sunucu aynı kuralı
            // uygular — takvim onu ÖNCEDEN gösterir ki onay mercii çıkmaz sokağa girmesin.
            maxDate={parseISODate(request?.parentDeadline ?? null) ?? undefined}
            // Talep edilen + mevcut tarih takvimde devre dışı: takvimi açmak "değiştir" niyetidir
            // (bkz. javadoc). Ön-dolu değer etkilenmez — takvim hiç açılmadan Onayla'ya basılabilir.
            disabledDates={disabledDates.length > 0 ? disabledDates : undefined}
            showIcon
            readOnlyInput
            // Panel dialog gövdesinin içinde kalırsa gövde kaydırma çubuğu açar ve takvim kırpılır.
            appendTo={document.body}
          />
        </FormField>
      </FormGrid>
    </FormDialog>
  )
}
